import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  CaretDown,
  ChartBar,
  CheckCircle,
  ClockClockwise,
  FunnelSimple,
  HourglassMedium,
  Icon,
  MagnifyingGlass,
  MapPin,
  Plus,
  Scroll,
  User,
} from 'phosphor-react';
import { toast } from 'sonner';
import { useAuth } from '../../../providers/auth';
import { PageBox } from '../../../components/pageBox';
import { useKanban, IKanbanCard, IKanbanColumn } from '../../../hooks/useKanban';
import {
  useLegalizacao,
  ILegalizacao,
  ILegalizacaoCreate,
  ILegalizacaoUpdate,
  TipoLegalizacao,
  TIPOS_LEGALIZACAO,
  TIPO_LEGALIZACAO_LABEL,
} from '../../../hooks/useLegalizacao';
import {
  colunasDoPlaybook,
  COR_TIPO,
  detalheDoErro,
  ETAPA_ASSUMIDA_KEY,
  fluxoDaEtapa,
  NivelRisco,
  proximaAcao,
  QuemDeve,
  QUEM_META,
  regimeDoProcesso,
  RISCO_META,
  tiposAlteracaoDoProcesso,
} from './legalizacaoPlaybook';
import { LegalizacaoArquivados } from './components/LegalizacaoArquivados';
import { LegalizacaoBoard } from './components/LegalizacaoBoard';
import { LegalizacaoCreateModal, IniciarAquiExtra } from './components/LegalizacaoCreateModal';
import { LegalizacaoDrawer } from './components/LegalizacaoDrawer';
import { LegalizacaoFinalizados } from './components/LegalizacaoFinalizados';
import { LegalizacaoLegenda } from './components/LegalizacaoLegenda';
import { LegalizacaoVisaoGeral } from './components/LegalizacaoVisaoGeral';

// Stat-card de contagem do header — gramática do ContaCil (chip de ícone tintado +
// número grande + rótulo), com entrada escalonada via framer-motion.
const KpiStat: React.FC<{
  valor: number;
  label: string;
  accent: string;
  icon: Icon;
  delay: number;
}> = ({ valor, label, accent, icon: IconComp, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay, ease: [0.4, 0, 0.2, 1] }}
  >
    <Flex
      align="center"
      gap={2.5}
      bg="white"
      borderWidth="1px"
      borderColor="gray.100"
      borderRadius="xl"
      px={3.5}
      py={2}
      boxShadow="0 1px 2px rgba(15,23,42,.05)"
      minW="118px"
    >
      <Flex
        w="34px"
        h="34px"
        borderRadius="lg"
        align="center"
        justify="center"
        bg={`${accent}14`}
        color={accent}
        flexShrink={0}
      >
        <IconComp size={18} weight="duotone" />
      </Flex>
      <Box>
        <Text
          fontSize="20px"
          fontWeight="700"
          lineHeight="1"
          color="gray.900"
          letterSpacing="-0.02em"
        >
          {valor}
        </Text>
        <Text
          fontSize="10px"
          color="gray.400"
          textTransform="uppercase"
          letterSpacing="0.05em"
          fontWeight="700"
          mt="3px"
        >
          {label}
        </Text>
      </Box>
    </Flex>
  </motion.div>
);

// ---------------------------------------------------------------------------
// Busca — normalização por natureza do campo, não uma regra só pra todos.
//
// `cnpj` e `nire` são numéricos e o formato GRAVADO é misto: o modal de criação faz
// strip de dígitos (LegalizacaoCreateModal), o drawer grava verbatim o que o operador
// digitou, o backend não normaliza e a coluna é String(18) — exatamente a largura da
// máscara "00.000.000/0000-00". Ou seja, o mesmo campo convive mascarado e cru no
// banco, e comparar substring ingênua erra metade dos casos. Comparo só os dígitos
// dos dois lados.
//
// `protocolo_numero` é ALFANUMÉRICO por design (o playbook usa "ex.: 5A21-2026").
// Aplicar o mesmo strip de dígitos o destruiria — "5A21-2026" viraria "212026" e a
// busca por "5A21" falharia. Esse compara texto sem separadores.
const soDigitos = (v?: string | null) => (v ?? '').replace(/\D/g, '');
const soAlfaNum = (v?: string | null) => (v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Valor do eixo "responsável" para o processo sem ninguém atribuído. */
const RESP_SEM = 'sem';

const ROTULO_SLA: Record<string, string> = {
  verde: 'No prazo',
  amarelo: 'Atenção',
  vermelho: 'Em risco',
};
// Cor do ponto de cada situação, na régua do card. "Em risco" cobre os níveis 2 e 3;
// usa o 3 por ser o sinal mais forte do conjunto.
const NIVEL_SLA: Record<string, NivelRisco> = { verde: 0, amarelo: 1, vermelho: 3 };

// Os rótulos de QUEM_META são minúsculos porque no card vivem dentro de um badge em
// caixa alta ("VOCÊ"). Em lista de menu, caixa de frase.
const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const rotuloQuem = (q: QuemDeve) =>
  q === 'indefinido' ? 'Sem etapa' : capitalizar(QUEM_META[q].rotulo);

/** Rótulo do menu de filtros: ponto colorido à esquerda, contagem à direita. */
const LinhaFiltro: React.FC<{ cor: string; rotulo: string; n?: number }> = ({ cor, rotulo, n }) => (
  <Flex align="center" justify="space-between" w="full" gap={4}>
    <Flex align="center" gap={2} minW={0}>
      <Box w="7px" h="7px" borderRadius="full" bg={cor} flexShrink={0} />
      <Text as="span" noOfLines={1}>
        {rotulo}
      </Text>
    </Flex>
    {n !== undefined && (
      <Text
        as="span"
        fontSize="11px"
        color="gray.500"
        fontWeight="700"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {n}
      </Text>
    )}
  </Flex>
);

/**
 * GRUPO SEGMENTADO dos filtros (2026-08-13).
 *
 * Os três eixos (estado, responsável, o menu com pendência+situação) viviam como botões
 * `ghost` soltos: texto cinza, sem borda nem fundo. Do lado de um campo de busca com
 * pílula branca e de um "Arquivados" roxo, eles liam como RÓTULO, não como controle — e o
 * elemento mais forte da barra era um atalho pra outra tela.
 *
 * Agrupados num bloco só, resolvem duas coisas de uma vez: ganham corpo (fundo branco +
 * borda, a mesma linguagem da busca) e passam a se anunciar como uma família — "isto aqui
 * recorta o quadro" —, separada do que não é filtro. As divisórias mantêm os alvos de
 * clique distintos, coisa que um bloco liso perderia.
 *
 * O ATIVO tinge só o próprio segmento: dizer qual eixo está filtrando é justamente o que
 * um único botão "Filtros (2)" não conseguia fazer.
 */
const segmentoFiltro = (ativo: boolean) => ({
  size: 'sm' as const,
  variant: 'ghost' as const,
  // Sem raio próprio: quem arredonda é o contêiner, que recorta as pontas. Raio aqui
  // deixaria cantos claros aparecendo por cima do fundo branco do grupo.
  borderRadius: 0,
  h: '32px',
  px: 3,
  fontSize: '13px',
  fontWeight: '700',
  color: ativo ? 'purple.600' : 'gray.600',
  bg: ativo ? 'purple.50' : 'transparent',
  _hover: { bg: ativo ? 'purple.100' : 'blackAlpha.50' },
  _active: { bg: ativo ? 'purple.100' : 'blackAlpha.100' },
});

/** Fio entre segmentos. Recuado em cima e embaixo pra não encostar na borda do grupo. */
const DivisorSegmento = () => <Box w="1px" my="6px" bg="gray.200" flexShrink={0} />;

const TituloGrupo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    px={3}
    pt={2}
    pb={1}
    fontSize="10px"
    fontWeight="700"
    color="gray.700"
    textTransform="uppercase"
    letterSpacing="0.06em"
  >
    {children}
  </Text>
);

export const LegalizacaoPage = () => {
  const { user: usuario } = useAuth();
  const { getProjects, getBoard } = useKanban();
  const { listarLegalizacoes, criarLegalizacao, atualizarLegalizacao, moverLegalizacao } =
    useLegalizacao();
  const queryClient = useQueryClient();

  const [tipo, setTipo] = useState<TipoLegalizacao>('constituicao');
  // Aba "Visão geral" (dashboard cross-tipo) vs board do tipo. Board é o landing.
  const [visao, setVisao] = useState(false);
  const [selectedLegalizacao, setSelectedLegalizacao] = useState<ILegalizacao | null>(null);
  const [selectedCard, setSelectedCard] = useState<IKanbanCard | null>(null);
  const [selectedCardColumn, setSelectedCardColumn] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [slaFiltro, setSlaFiltro] = useState('');
  // "Onde está a pendência" — null = todos. Seleção única: é modo de foco, não composição.
  const [quemFiltro, setQuemFiltro] = useState<QuemDeve | null>(null);
  /**
   * Recorte por UF da junta, PERSISTIDO por usuário.
   *
   * O operador que cuida só de determinadas juntas não deveria clicar num filtro toda
   * vez que abre a tela. Persistido, são zero cliques em regime — melhor que um
   * controle em destaque permanente, que custa uma faixa fixa E ainda um clique por
   * sessão.
   *
   * O risco de filtro grudento é o "cadê meus processos?". O antídoto já está na tela:
   * o gatilho do menu mostra o NOME do filtro ativo (não um contador) e o "N de M" fica
   * ao lado — filtro persistido fica visível, não escondido.
   *
   * ⚠️ Uma UF só (não a sigla): existe uma junta por estado, e é por UF que o
   * `resolverRegistry` decide o fluxo. Filtrar pela sigla criaria um segundo eixo pro
   * mesmo recorte, divergente sempre que os dois campos discordarem no banco.
   */
  const chaveUf = `legalizacao:ufFiltro:${usuario?.id ?? 'anon'}`;
  const [ufFiltro, setUfFiltro] = useState<string>(
    () => window.localStorage.getItem(`legalizacao:ufFiltro:${usuario?.id ?? 'anon'}`) ?? '',
  );
  const escolherUf = (uf: string) => {
    setUfFiltro(uf);
    if (uf) window.localStorage.setItem(chaveUf, uf);
    else window.localStorage.removeItem(chaveUf);
  };
  /**
   * Recorte por RESPONSÁVEL. `''` = todos, `'sem'` = processos sem responsável atribuído,
   * senão o `responsavel_id` em texto (o value do MenuOptionGroup é string).
   *
   * NÃO é persistido, ao contrário da UF. A UF é uma característica do PROCESSO e não muda
   * sozinha; o responsável muda por decisão de outra pessoa — um filtro grudento de
   * "meus processos" faria o card reatribuído desaparecer da tela de quem o largou sem
   * nenhum evento visível. Como foco da sessão, some ao recarregar, que é o certo.
   */
  const [respFiltro, setRespFiltro] = useState<string>('');
  // Busca DENTRO da lista de responsáveis (o pedido era "pesquisando pelo nome"). Separada
  // da busca geral: filtrar a lista de pessoas não é filtrar o quadro.
  const [buscaResp, setBuscaResp] = useState('');
  // "Iniciar aqui": coluna-alvo pendente pro modal de criação (null = criação normal).
  const [colunaAlvo, setColunaAlvo] = useState<string | null>(null);
  // Processo que a visão geral mandou abrir. Não dá pra abrir na hora: trocar de aba
  // troca a queryKey do board e das legalizações, então card e processo só existem
  // um ou dois renders depois. Guarda o id e deixa o efeito abrir quando os dados
  // daquele tipo chegarem.
  const [pendenteAbrir, setPendenteAbrir] = useState<number | null>(null);

  const { isOpen: isCardOpen, onOpen: openCard, onClose: closeCard } = useDisclosure();
  const { isOpen: isCreateOpen, onOpen: openCreate, onClose: closeCreate } = useDisclosure();

  // 1. Projeto do kanban do tipo ativo, resolvido pelo `origem_modulo` (o backend
  // cria 1 projeto por tipo: lgz_constituicao / lgz_alteracao / lgz_baixa — prefixo
  // curto porque a coluna origem_modulo é VARCHAR(20)).
  const { data: projectsData, isLoading: loadingProjects } = useQuery({
    queryKey: ['kanban-projects'],
    queryFn: getProjects,
    staleTime: 30_000,
  });
  const origemModulo = `lgz_${tipo}`;
  const projeto = (projectsData?.data ?? []).find(p => p.origem_modulo === origemModulo);
  const projetoId = projeto?.id;

  // 2. Board do projeto (colunas + cards).
  const { data: boardData, isLoading: loadingBoard } = useQuery({
    queryKey: ['kanban-board', projetoId],
    queryFn: () => getBoard(projetoId),
    enabled: !!projetoId,
    staleTime: 30_000,
  });

  // 3. Legalizações do tipo — pra juntar card ↔ processo por `kanban_card_id`
  // (o move precisa do id do PROCESSO, não do card).
  const { data: legData, isLoading: loadingLeg } = useQuery({
    queryKey: ['legalizacoes', tipo],
    queryFn: () => listarLegalizacoes(tipo),
    staleTime: 30_000,
  });
  const legByCardId = useMemo(() => {
    const map = new Map<number, ILegalizacao>();
    (legData?.data ?? []).forEach(leg => {
      if (leg.kanban_card_id != null) map.set(leg.kanban_card_id, leg);
    });
    return map;
  }, [legData]);

  const columns: IKanbanColumn[] = boardData?.data?.columns ?? [];
  const total = legData?.data?.length ?? 0;

  /**
   * QUADRO-ESTRUTURA para o tipo que ainda não tem projeto no backend.
   *
   * O projeto `lgz_<tipo>` nasce junto com a PRIMEIRA legalização daquele tipo
   * (`ensure_projeto`). Antes disso não havia coluna nenhuma pra desenhar, e a tela caía
   * num aviso central — sem etapas à vista e, principalmente, sem "Iniciar aqui". Era o
   * contrário do necessário: um tipo virgem é exatamente onde mais se cadastra processo
   * que já está no meio do caminho (a empresa não espera o sistema pra ir à junta).
   *
   * As colunas saem do PLAYBOOK, que por contrato tem os mesmos títulos que o backend vai
   * criar. Ids NEGATIVOS: não existem no banco, e um id negativo estoura cedo e alto se
   * algum dia vazar pra uma chamada de API, em vez de casar por engano com um id real.
   */
  const colunasPreview: IKanbanColumn[] = useMemo(
    () =>
      colunasDoPlaybook(tipo).map((titulo, i) => ({
        id: -(i + 1),
        titulo,
        rank: '',
        projeto_id: null,
        dt_criacao: '',
        dt_atualizacao: '',
        cards: [],
      })),
    [tipo],
  );
  const projectsLoaded = !!projectsData;
  const semProjeto = projectsLoaded && !projetoId;
  /**
   * Títulos das etapas, na ordem — o que o drawer e o modal de criação usam.
   *
   * ⚠️ Cai pro playbook quando o quadro ainda não existe. Sem isto o "Iniciar aqui" do
   * quadro-estrutura ficava MUDO: `LegalizacaoCreateModal` calcula
   * `alvoIdx = colunas.indexOf(colunaAlvo)` e trata `alvoIdx > 0` como "iniciar aqui" —
   * com a lista vazia o índice é -1, e o modal criaria um processo comum na primeira
   * etapa, ignorando a etapa escolhida sem avisar ninguém.
   */
  const colunas = useMemo(
    () => (columns.length ? columns.map(c => c.titulo) : colunasDoPlaybook(tipo)),
    [columns, tipo],
  );

  // Contagens do header (buckets disjuntos, do board do tipo ativo): concluídos =
  // última etapa + finalizados (fora do quadro); aguardando órgão = etapa de espera
  // externa (fluxo 'wait'); em andamento = o resto. Só conta cards que são legalizações.
  const kpis = useMemo(() => {
    const ultima = colunas[colunas.length - 1];
    let andamento = 0;
    let aguardando = 0;
    let concluidos = 0;
    columns.forEach(col => {
      const espera = fluxoDaEtapa(tipo, col.titulo).includes('wait');
      (col.cards ?? []).forEach(c => {
        const leg = legByCardId.get(c.id);
        // Finalizado entra pela lista do processo, logo abaixo — nunca duas vezes.
        if (!leg || leg.finalizado_em) return;
        if (col.titulo === ultima) concluidos += 1;
        else if (espera) aguardando += 1;
        else andamento += 1;
      });
    });
    concluidos += (legData?.data ?? []).filter(l => l.finalizado_em).length;
    return { andamento, aguardando, concluidos };
  }, [columns, colunas, tipo, legByCardId, legData]);

  /**
   * Cor do SLA que ainda VALE para o card. Na coluna final o processo já foi entregue: o
   * semáforo compara o prazo com hoje e acusaria atraso de quem entregou no prazo.
   */
  const ultimaColuna = colunas[colunas.length - 1];
  const corSlaViva = (c: IKanbanCard, coluna: string) =>
    coluna === ultimaColuna ? undefined : c.sla?.cor;

  // BASE: cards que passam na BUSCA. É o denominador comum das duas facetas — as
  // contagens do menu saem daqui, cada uma descontando só o OUTRO filtro (contagem de
  // faceta honesta: o número diz quanto sobraria se eu clicasse naquela opção).
  //
  // ⚠️ PRESERVA TODAS AS COLUNAS (map, nunca filter). O gate "avance uma etapa por vez"
  // do arrasto mede `Math.abs(toIdx - fromIdx)` sobre ESTE array (LegalizacaoBoard
  // `onDragEnd`). Esconder uma coluna faria duas etapas separadas virarem vizinhas e o
  // gate deixaria passar um salto. Filtrar card é seguro; filtrar coluna não é.
  const baseBusca = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return columns;
    const qDig = soDigitos(q);
    const qAlnum = soAlfaNum(q);
    return columns.map(col => ({
      ...col,
      cards: (col.cards ?? []).filter(c => {
        if ((c.titulo ?? '').toLowerCase().includes(q)) return true;
        // (o filtro de UF entra depois, junto com os outros eixos)
        const leg = legByCardId.get(c.id);
        if (!leg) return false;
        if ((leg.responsavel_nome ?? '').toLowerCase().includes(q)) return true;
        // 3+ caracteres: com 1 ou 2 o termo casaria quase toda a base e a busca
        // pareceria quebrada.
        if (qAlnum.length >= 3) {
          // O protocolo do processo é o do início (GOB/GOP, ou GON) até haver um novo.
          if ([leg.protocolo_numero, leg.gop, leg.gon].some(v => soAlfaNum(v).includes(qAlnum)))
            return true;
          // CNPJ por ALFANUMÉRICO, não por dígitos: o formato novo da Receita tem letras
          // nos 12 primeiros caracteres, e comparar só dígitos deixaria de casar o
          // número que o operador tem em mãos. Comparação por dígitos continua valendo
          // pro CNPJ clássico, que é subconjunto deste.
          if (soAlfaNum(leg.cnpj).includes(qAlnum)) return true;
        }
        // NIRE é numérico por definição (11 ou 12 dígitos).
        if (qDig.length >= 3 && soDigitos(leg.nire).includes(qDig)) return true;
        return false;
      }),
    }));
  }, [columns, busca, legByCardId]);

  // "Onde está a pendência" por card — UM cálculo só, reaproveitado pelas contagens e pelo
  // filtro. Chama `proximaAcao` com os MESMOS argumentos que o card usa pra imprimir o
  // rótulo (inclusive uf/município/tiposAlteracao, que mudam o checklist efetivo em
  // algumas UFs): recalcular por conta própria abriria a porta pra lista filtrada
  // discordar do que está escrito no próprio card.
  const quemPorCard = useMemo(() => {
    const m = new Map<number, QuemDeve>();
    baseBusca.forEach(col =>
      (col.cards ?? []).forEach(c => {
        const leg = legByCardId.get(c.id);
        if (!leg) return;
        const estado =
          (leg.estado_etapas as Record<string, Record<string, unknown>> | null)?.[col.titulo] ?? {};
        m.set(
          c.id,
          proximaAcao(tipo, col.titulo, estado, {
            uf: leg.junta_uf,
            municipio: leg.municipio,
            tiposAlteracao: tiposAlteracaoDoProcesso(leg.estado_etapas),
            regime: regimeDoProcesso(leg.estado_etapas),
          }).quem,
        );
      }),
    );
    return m;
  }, [baseBusca, legByCardId, tipo]);

  // Contagens de faceta. Cada eixo conta com o OUTRO eixo aplicado, nunca com ele
  // mesmo — senão a opção marcada mostraria o próprio resultado e as demais, zero.
  const ufDoCard = (cardId: number) => (legByCardId.get(cardId)?.junta_uf ?? '').toUpperCase();
  // Chave do eixo "responsável" para um card. Sem atribuição é um VALOR, não ausência de
  // valor: "quem está sem dono" é justamente uma das perguntas que o filtro responde.
  const respDoCard = (cardId: number) => {
    const id = legByCardId.get(cardId)?.responsavel_id;
    return id != null ? String(id) : RESP_SEM;
  };

  /**
   * id do responsável → nome. Sai da lista COMPLETA do tipo, não do recorte filtrado.
   *
   * Derivar do recorte parecia natural (é de lá que saem as contagens), mas quebrava
   * assim: com um responsável filtrado, uma busca sem resultado esvaziaria o mapa e o
   * botão voltaria a dizer "Responsável" — com o filtro ainda ligado — e o menu passaria a
   * mostrar "Usuário 42" no lugar do nome. Nome é atributo da pessoa; não depende de
   * quantos cards dela sobraram na tela.
   */
  const nomePorResp = useMemo(() => {
    const m: Record<string, string> = {};
    (legData?.data ?? []).forEach(l => {
      if (l.responsavel_id != null) {
        m[String(l.responsavel_id)] = l.responsavel_nome ?? `Usuário ${l.responsavel_id}`;
      }
    });
    return m;
  }, [legData]);

  /**
   * O filtro de responsável que REALMENTE age nesta aba.
   *
   * `respFiltro` guarda a escolha do operador e sobrevive à troca de aba — como a busca e
   * os demais eixos. Só que os outros significam a mesma coisa em qualquer quadro, e este
   * não: a pessoa escolhida pode não ter nenhum processo do tipo que está na tela. Quando
   * isso acontecia, o quadro esvaziava e o botão exibia **"Responsável"** — como se nada
   * estivesse filtrado —, porque o nome não estava no mapa deste tipo e o rótulo caía no
   * fallback. Filtro ativo com cara de inativo é o pior caso: parece quadro vazio.
   *
   * DERIVADO, e não um reset no `setTipo`: resetar descartaria a escolha também quando ela
   * continua válida — e a mesma pessoa costuma cuidar de constituição E alteração. Assim a
   * seleção fica preservada, não age onde não faz sentido, e volta a agir ao retornar.
   *
   * ⚠️ Enquanto `legData` não chegou, mantém a escolha. Decidir com o mapa vazio faria o
   * filtro piscar (desliga no carregamento, liga de novo) a cada invalidação de query.
   */
  const respEfetivo = useMemo(() => {
    if (!respFiltro || respFiltro === RESP_SEM) return respFiltro;
    if (!legData) return respFiltro;
    return respFiltro in nomePorResp ? respFiltro : '';
  }, [respFiltro, nomePorResp, legData]);

  const { contagemQuem, contagemSla, contagemUf, contagemResp } = useMemo(() => {
    const quem: Record<QuemDeve, number> = {
      equipe: 0,
      cliente: 0,
      orgao: 0,
      nenhum: 0,
      indefinido: 0,
    };
    const sla: Record<string, number> = { verde: 0, amarelo: 0, vermelho: 0 };
    const uf: Record<string, number> = {};
    const resp: Record<string, number> = {};
    baseBusca.forEach(col =>
      (col.cards ?? []).forEach(c => {
        const q = quemPorCard.get(c.id);
        if (q === undefined) return; // card sem legalização casada: o board também o remove
        const u = ufDoCard(c.id);
        const r = respDoCard(c.id);
        const cor = corSlaViva(c, col.titulo);
        const passaUf = !ufFiltro || u === ufFiltro;
        const passaSla = !slaFiltro || cor === slaFiltro;
        const passaQuem = !quemFiltro || q === quemFiltro;
        const passaResp = !respEfetivo || r === respEfetivo;
        if (passaSla && passaUf && passaResp) quem[q] += 1;
        if (passaQuem && passaUf && passaResp && cor) sla[cor] += 1;
        // A contagem por UF ignora o próprio filtro de UF — senão a UF marcada
        // mostraria o total e as outras zerariam, e trocar de recorte viraria adivinhação.
        if (passaQuem && passaSla && passaResp && u) uf[u] = (uf[u] ?? 0) + 1;
        if (passaQuem && passaSla && passaUf) resp[r] = (resp[r] ?? 0) + 1;
      }),
    );
    return { contagemQuem: quem, contagemSla: sla, contagemUf: uf, contagemResp: resp };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    baseBusca,
    quemPorCard,
    legByCardId,
    slaFiltro,
    quemFiltro,
    ufFiltro,
    respEfetivo,
    ultimaColuna,
  ]);

  // Resultado final. Card sem legalização casada fica de fora quando há filtro de bola —
  // é o mesmo critério que o board aplica no render (`legByCardId.has`), então a
  // contagem não diverge do que aparece na tela.
  const columnsFiltradas = useMemo(() => {
    if (!quemFiltro && !slaFiltro && !ufFiltro && !respEfetivo) return baseBusca;
    return baseBusca.map(col => ({
      ...col,
      cards: (col.cards ?? []).filter(
        c =>
          (!slaFiltro || corSlaViva(c, col.titulo) === slaFiltro) &&
          (!quemFiltro || quemPorCard.get(c.id) === quemFiltro) &&
          (!ufFiltro || ufDoCard(c.id) === ufFiltro) &&
          (!respEfetivo || respDoCard(c.id) === respEfetivo),
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    baseBusca,
    quemFiltro,
    slaFiltro,
    ufFiltro,
    respEfetivo,
    quemPorCard,
    legByCardId,
    ultimaColuna,
  ]);

  /**
   * Dois conceitos, porque o filtro de UF é PERSISTIDO e os outros não.
   *
   * `filtroTransitorio` = o operador acabou de restringir a vista nesta sessão.
   * `filtroAtivo`       = a vista está restrita por qualquer motivo, inclusive o
   *                       recorte de estado que ele deixou gravado.
   *
   * ⚠️ Misturar os dois quebrava duas coisas de forma permanente. O "Iniciar aqui" é
   * escondido com filtro ativo (coluna esvaziada por filtro não está vazia, está
   * escondida) — mas como a UF volta do localStorage a cada abertura, quem escolhesse um
   * estado UMA vez perdia o botão em todas as sessões seguintes. E o auto-scroll, que
   * pula pro primeiro resultado, passava a disparar em toda montagem e a cada
   * invalidação de query, jogando o quadro de volta pro início sem o operador ter
   * filtrado nada.
   *
   * Ação primária e movimento de tela seguem o TRANSITÓRIO; texto e contagem seguem o
   * ativo, porque a ambiguidade da coluna vazia existe nos dois casos.
   */
  const filtroTransitorio = !!busca.trim() || !!slaFiltro || !!quemFiltro || !!respEfetivo;
  const filtroAtivo = filtroTransitorio || !!ufFiltro;
  // Rótulos dos filtros do MENU. A busca fica de fora de propósito: ela está visível no
  // próprio campo, e contá-la faria o gatilho anunciar um filtro que o operador já vê.
  // Só o que está DENTRO deste menu. A UF ficou de fora junto com a busca: as duas têm
  // controle próprio na barra, mostrando o próprio estado. Contá-las aqui faria o botão
  // dizer "2 filtros" com uma opção só marcada dentro dele, ou repetir "GO" ao lado de
  // um botão que já diz "GO".
  const filtrosMenuAtivos = [
    quemFiltro ? rotuloQuem(quemFiltro) : null,
    slaFiltro ? ROTULO_SLA[slaFiltro] : null,
  ].filter(Boolean) as string[];
  // UFs que aparecem no tipo ativo. Inclui a que está filtrada mesmo que ela zere no
  // recorte atual — senão o operador filtra por GO, digita uma busca sem resultado em
  // GO, e a opção marcada some da lista, deixando-o sem como voltar.
  const ufsPresentes = useMemo(() => {
    const set = new Set<string>(Object.keys(contagemUf));
    if (ufFiltro) set.add(ufFiltro);
    return Array.from(set).sort();
  }, [contagemUf, ufFiltro]);

  /**
   * Responsáveis que APARECEM no tipo ativo — não a lista de usuários do sistema.
   *
   * O `ResponsavelPicker` (atribuição) consulta `useUsers`, porque lá a pergunta é "quem
   * pode receber este processo". Aqui a pergunta é outra: "de quem são os processos que
   * estão neste quadro". Listar o sistema inteiro encheria o filtro de gente com zero
   * processo e ainda custaria uma requisição por abertura.
   *
   * Mesma regra da UF: quem está filtrado permanece na lista mesmo zerando no recorte
   * atual, senão a opção marcada some e não há como desmarcá-la.
   */
  const responsaveisPresentes = useMemo(() => {
    const chaves = new Set<string>(Object.keys(contagemResp));
    if (respEfetivo) chaves.add(respEfetivo);
    const q = buscaResp.trim().toLowerCase();
    return Array.from(chaves)
      .map(valor => ({
        valor,
        nome: valor === RESP_SEM ? 'Sem responsável' : (nomePorResp[valor] ?? `Usuário ${valor}`),
        n: contagemResp[valor] ?? 0,
      }))
      .filter(r => !q || r.nome.toLowerCase().includes(q))
      .sort((a, b) => {
        // "Sem responsável" por último: é a exceção da lista, não um colega chamado "Sem".
        if (a.valor === RESP_SEM) return 1;
        if (b.valor === RESP_SEM) return -1;
        return a.nome.localeCompare(b.nome, 'pt-BR');
      });
  }, [contagemResp, nomePorResp, respEfetivo, buscaResp]);

  // Rótulo curto do gatilho: só o primeiro nome cabe num botão de barra.
  const rotuloResp =
    respEfetivo === RESP_SEM
      ? 'Sem responsável'
      : (nomePorResp[respEfetivo] ?? '').split(' ')[0] || 'Responsável';

  const [primeiroFiltro] = filtrosMenuAtivos;
  let rotuloGatilho = 'Filtros';
  if (filtrosMenuAtivos.length === 1) rotuloGatilho = primeiroFiltro;
  else if (filtrosMenuAtivos.length > 1) rotuloGatilho = `${filtrosMenuAtivos.length} filtros`;
  const contarCards = (cols: IKanbanColumn[]) =>
    cols.reduce((n, col) => n + (col.cards ?? []).filter(c => legByCardId.has(c.id)).length, 0);
  const totalVisivel = contarCards(columnsFiltradas);
  const totalGeral = contarCards(columns);
  const limparFiltros = () => {
    setBusca('');
    setSlaFiltro('');
    escolherUf('');
    setQuemFiltro(null);
    setRespFiltro('');
    setBuscaResp('');
  };

  const carregando = loadingProjects || loadingLeg || (!!projetoId && loadingBoard);

  const fecharCriacao = () => {
    closeCreate();
    setColunaAlvo(null);
  };

  // ---- Mutations ----
  // Criação normal (1ª coluna) OU "Iniciar aqui" (cria → marca etapas anteriores como
  // assumidas + grava campos-chave → move pra etapa-alvo). O backend aceita salto de
  // coluna direto (mover_card_para não força adjacência).
  const createMutation = useMutation({
    mutationFn: async (vars: { payload: ILegalizacaoCreate; extra?: IniciarAquiExtra }) => {
      const res = await criarLegalizacao(vars.payload);
      const novo = res.data;
      if (vars.extra && novo?.id) {
        const estado: Record<string, unknown> = {};
        vars.extra.assumidas.forEach(col => {
          estado[col] = { [ETAPA_ASSUMIDA_KEY]: true };
        });
        const patch = {
          ...vars.extra.camposChave,
          estado_etapas: estado,
        } as unknown as ILegalizacaoUpdate;
        try {
          await atualizarLegalizacao(novo.id, patch);
          await moverLegalizacao(novo.id, { coluna_titulo: vars.extra.colunaAlvo });
        } catch {
          // Criou, mas o pós-config (assumidas / mover pra etapa-alvo) falhou. NÃO deixa
          // órfão invisível: sinaliza parcial → o card aparece na 1ª etapa e o operador
          // ajusta manualmente, em vez de recriar (e duplicar) o processo.
          return { parcial: true };
        }
      }
      return { parcial: false };
    },
    onSuccess: (result, vars) => {
      // O 1º processo de um tipo cria o projeto → invalida a lista de projetos.
      queryClient.invalidateQueries({ queryKey: ['kanban-projects'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      // Prefixo `['legalizacoes']` invalida per-tipo E o dashboard `['legalizacoes','todas']`.
      queryClient.invalidateQueries({ queryKey: ['legalizacoes'] });
      if (result.parcial) {
        toast.warning(
          'Processo criado, mas não foi posicionado/preenchido. Ele está na 1ª etapa — ' +
            'ajuste manualmente (não recrie, pra não duplicar).',
        );
      } else {
        toast.success(
          vars.extra ? `Processo criado em "${vars.extra.colunaAlvo}"` : 'Processo criado',
        );
      }
      fecharCriacao();
    },
    // O POST agora pode recusar por REGRA (422 do invariante de CNPJ). Genérico faria o
    // operador tentar de novo com o mesmo dado.
    onError: (erro: unknown) =>
      toast.error(detalheDoErro(erro, 'Não foi possível criar o processo. Tente novamente.')),
  });

  // ---- Handlers ----
  // Sem drag: o movimento entre etapas é pelo GATE "Avançar" do drawer (transição
  // de status via endpoint dedicado). O board só abre o processo.
  const handleCardClick = (card: IKanbanCard, leg: ILegalizacao) => {
    setSelectedCard(card);
    setSelectedLegalizacao(leg);
    const col = columns.find(c => c.cards.some(cc => cc.id === card.id));
    setSelectedCardColumn(col?.titulo ?? null);
    openCard();
  };

  // Visão geral → quadro: troca a aba e marca o processo pra abrir.
  const handleAbrirDaVisaoGeral = (legId: number, tipoAlvo: TipoLegalizacao) => {
    setVisao(false);
    setTipo(tipoAlvo);
    setPendenteAbrir(legId);
  };

  // Abre o modal assim que o board + as legalizações do tipo alvo terminarem de
  // carregar. Limpa o pendente em TODOS os caminhos — inclusive quando o card não
  // é achado — senão o id fica preso e dispara na próxima troca de aba.
  useEffect(() => {
    if (pendenteAbrir == null) return;
    // Voltou pra visão geral antes de o quadro carregar: a intenção morreu. Sem isto o
    // id ficava guardado e disparava DEPOIS, na próxima vez que o operador entrasse num
    // quadro — abrindo um card que ele não pediu, sem relação com o que estava fazendo.
    if (visao) {
      setPendenteAbrir(null);
      return;
    }
    if (carregando) return;
    const leg = (legData?.data ?? []).find(l => l.id === pendenteAbrir);
    const card =
      leg?.kanban_card_id != null
        ? columns.flatMap(c => c.cards ?? []).find(c => c.id === leg.kanban_card_id)
        : undefined;
    setPendenteAbrir(null);
    if (!leg || !card) {
      // O quadro do tipo certo já está na tela; só o card é que não foi localizado
      // (processo sem card, ou board defasado). Dizer isso é melhor que um clique
      // que aparenta não ter feito nada.
      toast.info('Processo não encontrado no quadro. Atualize a página e tente de novo.');
      return;
    }
    handleCardClick(card, leg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendenteAbrir, visao, carregando, legData, columns]);

  /**
   * O card e o processo ABERTOS no drawer, relidos do cache a cada render.
   *
   * ⚠️ `selectedCard`/`selectedLegalizacao` guardam o objeto do INSTANTE DO CLIQUE. Enquanto
   * o drawer está aberto, qualquer gravação invalida as queries e o quadro se atualiza — mas
   * o drawer continuava exibindo o objeto congelado. Ficou visível ao renomear a empresa: o
   * toast dizia "Nome atualizado", o banco tinha o nome novo, e o cabeçalho seguia com o
   * antigo até fechar e reabrir.
   *
   * Vale para TODO campo do card e do processo, não só o título — o defeito é pré-existente,
   * a renomeação só o tornou óbvio porque o título fica no topo da tela.
   *
   * O `??` mantém o snapshot como reserva: durante um refetch a lista some por um instante, e
   * sem ele o drawer piscaria vazio.
   */
  const cardAberto = useMemo(() => {
    if (!selectedCard) return null;
    return columns.flatMap(c => c.cards ?? []).find(c => c.id === selectedCard.id) ?? selectedCard;
  }, [columns, selectedCard]);
  const legalizacaoAberta = useMemo(() => {
    if (!selectedLegalizacao) return null;
    return (legData?.data ?? []).find(l => l.id === selectedLegalizacao.id) ?? selectedLegalizacao;
  }, [legData, selectedLegalizacao]);

  const handleCreate = (payload: ILegalizacaoCreate, extra?: IniciarAquiExtra) =>
    createMutation.mutate({ payload, extra });

  // "Novo processo" cria na 1ª coluna; "Iniciar aqui" mira a coluna clicada.
  const abrirCriacaoNormal = () => {
    setColunaAlvo(null);
    openCreate();
  };
  const handleIniciarAqui = (col: string) => {
    setColunaAlvo(col);
    openCreate();
  };

  return (
    <PageBox>
      <Box minH="100vh" bg="gray.50">
        {/* Header */}
        <Box
          bg="white"
          borderBottomWidth="1px"
          borderColor="gray.100"
          px={{ base: 4, md: 8 }}
          py={5}
          position="sticky"
          top={0}
          zIndex={10}
        >
          <Flex
            align={{ base: 'flex-start', md: 'center' }}
            justify="space-between"
            gap={4}
            flexDir={{ base: 'column', md: 'row' }}
            mb={4}
          >
            <Flex align="center" gap={3}>
              <Box
                w="52px"
                h="52px"
                borderRadius="2xl"
                bg="linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)"
                boxShadow="0 8px 22px -6px rgba(124,58,237,.32)"
                color="#7C3AED"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Scroll size={28} weight="duotone" />
              </Box>
              <Box>
                <Text
                  fontSize="10px"
                  color="gray.400"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                >
                  Módulo
                </Text>
                <Heading fontSize="18px" fontWeight="700" color="gray.900" letterSpacing="-0.02em">
                  Legalização — Junta Comercial
                </Heading>
                <Text fontSize="11px" color="gray.500" mt={0.5}>
                  {visao
                    ? 'Panorama geral — constituição, alteração e baixa'
                    : `${total} ${total === 1 ? 'processo' : 'processos'} de ${TIPO_LEGALIZACAO_LABEL[
                        tipo
                      ].toLowerCase()} · cada card percorre as etapas na junta comercial`}
                </Text>
              </Box>
            </Flex>

            <Flex align="center" gap={3} wrap="wrap">
              {!visao && !carregando && !semProjeto && (
                <Flex gap={2.5}>
                  <KpiStat
                    valor={kpis.andamento}
                    label="Em andamento"
                    accent="#1B88F5"
                    icon={ClockClockwise}
                    delay={0}
                  />
                  <KpiStat
                    valor={kpis.aguardando}
                    label="Aguard. órgão"
                    accent="#F59E0B"
                    icon={HourglassMedium}
                    delay={0.06}
                  />
                  <KpiStat
                    valor={kpis.concluidos}
                    label="Concluídos"
                    accent="#10B981"
                    icon={CheckCircle}
                    delay={0.12}
                  />
                </Flex>
              )}
              <Button
                colorScheme="purple"
                borderRadius="full"
                px={5}
                boxShadow="0 8px 20px -8px rgba(124,58,237,.5)"
                leftIcon={<Plus size={16} weight="bold" />}
                onClick={abrirCriacaoNormal}
              >
                Novo processo
              </Button>
            </Flex>
          </Flex>

          {/* Segmented control: Visão geral (dashboard) + abas por tipo (cor por tipo) */}
          <Flex
            bg="rgba(15,23,42,0.04)"
            borderRadius="full"
            p="4px"
            gap="3px"
            w="fit-content"
            flexWrap="wrap"
          >
            <Button
              variant="unstyled"
              h="34px"
              px={4}
              borderRadius="full"
              display="inline-flex"
              alignItems="center"
              gap={2}
              fontSize="13px"
              fontWeight={visao ? '700' : '600'}
              bg={visao ? '#7C3AED' : 'transparent'}
              color={visao ? 'white' : 'gray.500'}
              boxShadow={visao ? '0 2px 8px -2px rgba(15,23,42,0.28)' : 'none'}
              _hover={visao ? {} : { color: 'gray.700' }}
              transition="all .16s"
              onClick={() => setVisao(true)}
            >
              <ChartBar size={14} weight={visao ? 'fill' : 'regular'} />
              Visão geral
            </Button>
            {TIPOS_LEGALIZACAO.map(t => {
              const active = !visao && tipo === t;
              const cor = COR_TIPO[t];
              return (
                <Button
                  key={t}
                  variant="unstyled"
                  h="34px"
                  px={4}
                  borderRadius="full"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  fontSize="13px"
                  fontWeight={active ? '700' : '600'}
                  bg={active ? cor.solid : 'transparent'}
                  color={active ? 'white' : 'gray.500'}
                  boxShadow={active ? '0 2px 8px -2px rgba(15,23,42,0.28)' : 'none'}
                  _hover={active ? {} : { color: 'gray.700' }}
                  transition="all .16s"
                  onClick={() => {
                    setVisao(false);
                    setTipo(t);
                  }}
                >
                  <Box
                    w="9px"
                    h="9px"
                    borderRadius="full"
                    bg={active ? 'whiteAlpha.900' : cor.solid}
                  />
                  {TIPO_LEGALIZACAO_LABEL[t]}
                </Button>
              );
            })}
          </Flex>
        </Box>

        {/* Body */}
        <Box px={{ base: 4, md: 6 }} py={6}>
          {visao && <LegalizacaoVisaoGeral onAbrirProcesso={handleAbrirDaVisaoGeral} />}

          {!visao && carregando && (
            <Flex justify="center" py={20}>
              <Spinner size="xl" color="purple.500" thickness="3px" />
            </Flex>
          )}

          {/* Tipo sem nenhum processo: em vez do aviso central que existia aqui — sem
              etapas à vista e sem como cadastrar um processo já em andamento — mostra a
              ESTRUTURA do quadro. As colunas vêm do playbook (ver `colunasPreview`), então
              o "Iniciar aqui" existe desde o primeiro acesso, que é quando ele mais serve.
              A faixa explica que o quadro está vazio; sem ela, oito colunas com "— vazio —"
              pareceriam falha de carregamento. */}
          {!visao && !carregando && semProjeto && (
            <>
              <Flex
                align="center"
                gap={3}
                mb={4}
                px={4}
                py={3}
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.100"
                wrap="wrap"
              >
                <Flex
                  w="34px"
                  h="34px"
                  borderRadius="lg"
                  bg="gray.100"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Plus size={17} color="#9CA3AF" weight="light" />
                </Flex>
                <Box flex="1" minW="220px">
                  <Text fontSize="13px" fontWeight="500" color="gray.700">
                    Nenhum processo de {TIPO_LEGALIZACAO_LABEL[tipo].toLowerCase()} ainda
                  </Text>
                  <Text color="gray.400" fontSize="11px" mt={0.5}>
                    Abaixo estão as etapas deste tipo. Comece pelo início com <b>Novo processo</b> —
                    ou, se o processo já está em andamento, use <b>Iniciar aqui</b> na etapa em que
                    ele está.
                  </Text>
                </Box>
                <Button
                  colorScheme="purple"
                  borderRadius="full"
                  size="sm"
                  fontSize="13px"
                  px={5}
                  flexShrink={0}
                  leftIcon={<Plus size={14} weight="bold" />}
                  onClick={abrirCriacaoNormal}
                >
                  Novo processo
                </Button>
              </Flex>
              {/* Sem barra de filtros: não há o que filtrar, e o contador de arquivados
                  depende de um projeto que ainda não existe. */}
              <LegalizacaoBoard
                columns={colunasPreview}
                legByCardId={legByCardId}
                tipo={tipo}
                onCardClick={handleCardClick}
                onIniciarAqui={handleIniciarAqui}
              />
            </>
          )}

          {!visao && !carregando && !semProjeto && (
            <>
              {/* Filtros: busca ampla + onde está a pendência + situação (SLA). */}
              <Flex gap={3} mb={4} align="center" wrap="wrap">
                <InputGroup maxW="320px" size="sm">
                  <InputLeftElement pointerEvents="none">
                    <MagnifyingGlass size={15} color="#94A3B8" />
                  </InputLeftElement>
                  <Input
                    placeholder="Empresa, CNPJ, NIRE, protocolo..."
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    borderRadius="full"
                    // Explícito, embora `size="sm"` já defina um tamanho: o padrão do Chakra
                    // para `sm` é 14px, e ele entraria na barra como um QUINTO tamanho sem
                    // nunca aparecer numa busca por `fontSize` — que é exatamente como a
                    // escala divergiu antes. Controle acompanha controle: 13px, como as abas.
                    fontSize="13px"
                    bg="white"
                    borderColor="gray.200"
                    boxShadow="0 1px 2px rgba(15,23,42,.04)"
                    _hover={{ borderColor: 'gray.300' }}
                    _focusVisible={{ borderColor: '#7C3AED', boxShadow: '0 0 0 1px #7C3AED' }}
                  />
                </InputGroup>

                {/* ESTADO em controle PRÓPRIO, fora do menu de filtros.
                    Os outros dois eixos têm 4 e 3 opções fixas; a UF cresce com a
                    carteira. Com 7 estados o menu único passaria de 13 pra 21 linhas e
                    encostaria no rodapé da página — e a lista que mais cresce é
                    justamente a que o operador mais usa.

                    Separado, ele tem rolagem própria e o menu de filtros volta a ter
                    altura fixa. É também o "acesso facilitado" que faltava: um clique
                    em vez de dois, com o estado atual escrito no botão.

                    SEMPRE VISÍVEL nas três abas (decisão do user, 2026-08-12). A versão
                    anterior escondia com menos de duas UFs no quadro, e o efeito era o
                    botão existir em alteração e sumir em constituição — o operador não
                    tem como saber que a diferença é o dado, e não a tela. Controle que
                    aparece e some conforme o conteúdo custa mais em confiança do que
                    economiza em pixels. */}
                <Flex
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="full"
                  boxShadow="0 1px 2px rgba(15,23,42,.04)"
                  overflow="hidden"
                  flexShrink={0}
                >
                  <Menu placement="bottom-start">
                    <MenuButton
                      as={Button}
                      {...segmentoFiltro(!!ufFiltro)}
                      leftIcon={<MapPin size={14} weight={ufFiltro ? 'fill' : 'bold'} />}
                      rightIcon={<CaretDown size={11} weight="bold" />}
                    >
                      {ufFiltro || 'Estado'}
                    </MenuButton>
                    <MenuList
                      minW="200px"
                      maxH="320px"
                      overflowY="auto"
                      py={1}
                      borderColor="gray.200"
                      boxShadow="0 8px 28px rgba(15,23,42,.14)"
                      fontSize="13px"
                      sx={{
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(15,23,42,.16)',
                          borderRadius: '999px',
                        },
                      }}
                    >
                      <MenuOptionGroup
                        type="radio"
                        value={ufFiltro}
                        onChange={v => escolherUf(v as string)}
                      >
                        <MenuItemOption value="">
                          <LinhaFiltro cor="transparent" rotulo="Todos os estados" />
                        </MenuItemOption>
                        {ufsPresentes.map(uf => (
                          <MenuItemOption key={uf} value={uf}>
                            <LinhaFiltro cor="#0E7490" rotulo={uf} n={contagemUf[uf] ?? 0} />
                          </MenuItemOption>
                        ))}
                      </MenuOptionGroup>
                    </MenuList>
                  </Menu>

                  <DivisorSegmento />

                  {/* RESPONSÁVEL em controle próprio, pela mesma razão da UF: a lista cresce
                      com a equipe, e um eixo que cresce não cabe num menu de altura fixa.
                      Traz busca por nome dentro do menu — com o time inteiro no quadro,
                      percorrer a lista com o olho é mais lento que digitar três letras.
                      A lista é dos responsáveis PRESENTES no quadro, não dos usuários do
                      sistema (ver `responsaveisPresentes`). */}
                  <Menu placement="bottom-start" isLazy>
                    <MenuButton
                      as={Button}
                      {...segmentoFiltro(!!respEfetivo)}
                      maxW="180px"
                      leftIcon={<User size={14} weight={respEfetivo ? 'fill' : 'bold'} />}
                      rightIcon={<CaretDown size={11} weight="bold" />}
                      sx={{ '& > span': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                    >
                      {respEfetivo ? rotuloResp : 'Responsável'}
                    </MenuButton>
                    <MenuList
                      minW="252px"
                      py={1}
                      borderColor="gray.200"
                      boxShadow="0 8px 28px rgba(15,23,42,.14)"
                      fontSize="13px"
                    >
                      <Box px={2} pb={2} pt={1}>
                        <Input
                          size="sm"
                          fontSize="13px"
                          borderRadius="md"
                          placeholder="Buscar pelo nome..."
                          value={buscaResp}
                          onChange={e => setBuscaResp(e.target.value)}
                          // O Menu do Chakra usa teclas pra navegar entre itens; sem isto,
                          // digitar dentro do campo pularia o foco pros itens da lista.
                          onKeyDown={e => e.stopPropagation()}
                        />
                      </Box>
                      <Box maxH="300px" overflowY="auto">
                        <MenuOptionGroup
                          type="radio"
                          value={respEfetivo}
                          onChange={v => setRespFiltro(v as string)}
                        >
                          <MenuItemOption value="">
                            <LinhaFiltro cor="transparent" rotulo="Todos" />
                          </MenuItemOption>
                          {responsaveisPresentes.map(r => (
                            <MenuItemOption key={r.valor} value={r.valor}>
                              <LinhaFiltro
                                cor={r.valor === RESP_SEM ? 'transparent' : '#7C3AED'}
                                rotulo={r.nome}
                                n={r.n}
                              />
                            </MenuItemOption>
                          ))}
                        </MenuOptionGroup>
                        {responsaveisPresentes.length === 0 && (
                          <Text px={3} py={2} fontSize="11px" color="gray.400">
                            {buscaResp.trim() ? 'Nenhum nome com esse termo' : 'Nenhum responsável'}
                          </Text>
                        )}
                      </Box>
                    </MenuList>
                  </Menu>

                  <DivisorSegmento />

                  {/* UM controle pros dois eixos, em vez de cinco chips + um <select>.
                      O <select> nativo, além de ocupar 200px, é renderizado pelo SISTEMA
                      OPERACIONAL — a lista aberta (azul, fonte do SO) não aceita estilo
                      nenhum. Menu do Chakra é HTML comum, então combina com a tela.

                      O gatilho mostra O QUE está filtrado, não só quantos: com um filtro
                      ativo ele vira o próprio rótulo. Um "Filtros (1)" obrigaria a abrir
                      pra descobrir o que está escondendo card. */}
                  <Menu closeOnSelect={false} placement="bottom-start">
                    <MenuButton
                      as={Button}
                      {...segmentoFiltro(filtrosMenuAtivos.length > 0)}
                      leftIcon={
                        <FunnelSimple
                          size={14}
                          weight={filtrosMenuAtivos.length ? 'fill' : 'bold'}
                        />
                      }
                      rightIcon={<CaretDown size={11} weight="bold" />}
                    >
                      {rotuloGatilho}
                    </MenuButton>
                    <MenuList
                      minW="248px"
                      // Teto de altura por garantia: "onde está a pendência" pode chegar a 6
                      // linhas quando aparecem "pronto" e "sem etapa".
                      maxH="70vh"
                      overflowY="auto"
                      py={1}
                      borderColor="gray.200"
                      boxShadow="0 8px 28px rgba(15,23,42,.14)"
                      fontSize="13px"
                    >
                      <TituloGrupo>Onde está a pendência</TituloGrupo>
                      <MenuOptionGroup
                        type="radio"
                        value={quemFiltro ?? ''}
                        onChange={v => setQuemFiltro((v as string) === '' ? null : (v as QuemDeve))}
                      >
                        <MenuItemOption value="">
                          <LinhaFiltro cor="transparent" rotulo="Todos" />
                        </MenuItemOption>
                        {/* "pronto" e "sem etapa" só entram quando existem: fixos, seriam
                          duas linhas zeradas permanentes; omitidos quando ocorrem,
                          esconderiam a anomalia — "sem etapa" é o card cuja coluna não
                          bate com o playbook e que está com o avanço TRAVADO. */}
                        {(['equipe', 'cliente', 'orgao', 'nenhum', 'indefinido'] as QuemDeve[])
                          .filter(
                            q =>
                              q === 'equipe' || q === 'cliente' || q === 'orgao' || contagemQuem[q],
                          )
                          .map(q => (
                            <MenuItemOption key={q} value={q}>
                              <LinhaFiltro
                                cor={QUEM_META[q].cor}
                                rotulo={
                                  q === 'indefinido'
                                    ? 'Sem etapa configurada'
                                    : capitalizar(QUEM_META[q].rotulo)
                                }
                                n={contagemQuem[q]}
                              />
                            </MenuItemOption>
                          ))}
                      </MenuOptionGroup>

                      <MenuDivider my={1} />
                      <TituloGrupo>Situação (prazo)</TituloGrupo>
                      <MenuOptionGroup
                        type="radio"
                        value={slaFiltro}
                        onChange={v => setSlaFiltro(v as string)}
                      >
                        <MenuItemOption value="">
                          <LinhaFiltro cor="transparent" rotulo="Todas" />
                        </MenuItemOption>
                        {/* "vermelho" do backend = crítico E atrasado. Rotular de
                          "Atrasado" fazia o filtro trazer críticos junto, contradizendo
                          a régua do card e o painel da visão geral, que separam os dois.
                          "Em risco" é o mesmo termo que o KPI e a lista da visão geral
                          usam pro mesmo conjunto (níveis 2+3). */}
                        {['verde', 'amarelo', 'vermelho'].map(cor => (
                          <MenuItemOption key={cor} value={cor}>
                            <LinhaFiltro
                              cor={RISCO_META[NIVEL_SLA[cor]].accent}
                              rotulo={ROTULO_SLA[cor]}
                              n={contagemSla[cor]}
                            />
                          </MenuItemOption>
                        ))}
                      </MenuOptionGroup>

                      {filtroAtivo && (
                        <>
                          <MenuDivider my={1} />
                          <MenuItem
                            onClick={limparFiltros}
                            fontSize="13px"
                            fontWeight="500"
                            color="gray.600"
                          >
                            Limpar filtros{busca.trim() ? ' e busca' : ''}
                          </MenuItem>
                        </>
                      )}
                    </MenuList>
                  </Menu>
                </Flex>

                {/* Quanto o filtro escondeu. Sem isto, filtro ligado tira card da tela
                    sem dizer nada — quem esquece um filtro marcado conclui que processo
                    sumiu. */}
                {filtroAtivo && (
                  <Text
                    fontSize="11px"
                    color="gray.600"
                    fontWeight="500"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {totalVisivel} de {totalGeral}
                  </Text>
                )}

                {/* NÃO SÃO FILTROS — por isso ficam fora do grupo e encostados na borda
                    direita. Os dois levam pra outro lugar (uma lista de arquivados, uma
                    referência); nenhum recorta o quadro que está na tela. Misturados aos
                    filtros, disputavam a mesma leitura, e o "Arquivados" ainda vinha
                    tingido de roxo — mais forte que os controles que o operador de fato usa
                    o dia inteiro. A distância faz o trabalho que a cor fazia errado. */}
                <Flex ml="auto" align="center" gap={2} flexShrink={0}>
                  <LegalizacaoFinalizados
                    projetoId={projetoId}
                    tipo={tipo}
                    legByCardId={legByCardId}
                  />
                  {/* Contador + acesso aos arquivados; fica aqui e nao no rodape
                      do board pra ser achavel e mostrar o total sem abrir nada. */}
                  <LegalizacaoArquivados
                    projetoId={projetoId}
                    tipo={tipo}
                    legByCardId={legByCardId}
                  />
                  {/* Legenda virou popover e mora aqui, no fim da barra de filtros:
                      e' referencia, nao informacao corrente. Recupera a faixa que ela
                      ocupava acima do board. */}
                  <LegalizacaoLegenda />
                </Flex>
              </Flex>
              <LegalizacaoBoard
                columns={columnsFiltradas}
                legByCardId={legByCardId}
                tipo={tipo}
                busca={busca}
                filtroAtivo={filtroAtivo}
                filtroTransitorio={filtroTransitorio}
                onCardClick={handleCardClick}
                onIniciarAqui={handleIniciarAqui}
              />
            </>
          )}
        </Box>
      </Box>

      <LegalizacaoDrawer
        isOpen={isCardOpen}
        onClose={closeCard}
        legalizacao={legalizacaoAberta}
        card={cardAberto}
        columnTitle={selectedCardColumn}
        colunas={colunas}
        tipo={tipo}
      />

      <LegalizacaoCreateModal
        isOpen={isCreateOpen}
        onClose={fecharCriacao}
        tipo={tipo}
        onSubmit={handleCreate}
        isSubmitting={createMutation.isPending}
        colunaAlvo={colunaAlvo}
        colunas={colunas}
      />
    </PageBox>
  );
};

export default LegalizacaoPage;
